import React, { useEffect, useMemo } from "react";
import FormHelperText from "@mui/material/FormHelperText";
import FormDivider from "@components/FormDivider/FormDivider";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import { shouldShowCargoOnboardSection } from "@features/vessel-call/cargoOnboard";
import { buildPortOptions } from "@features/vessel-call/create/utils/portOptions.ts";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { getVesselConditions, REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { Nationality, NatureOfCall } from "@services/vessel-call/types";
import { type Company, type PortCode } from "@services/vessel-call/types";
import type { VesselConditions } from "@services/vessel/types";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import {
  FORM_FIELDS,
  methodAtImo2020ComplianceValues,
  NATURE_OF_CALL,
  NATURE_OF_CALL_ERROR_FIELD,
  NATURE_OF_CALL_SPECIFY,
} from "../../constants";
import type { VesselCallOutletContext } from "../../CreateVesselCallContainer";
import { type VesselCallFormData } from "../../schema/vesselCallFormSchema";
import CargoOnboard from "./CargoOnboard";

const LANG_BASE = "vessel-call.create";

const NATURE_OF_CALL_SPECIFY_BY_FLAG = {
  [NATURE_OF_CALL.CARGO_OPERATION]: NATURE_OF_CALL_SPECIFY.CARGO_OPETATION_SPECIFY,
  [NATURE_OF_CALL.WASTE_DISPOSAL]: NATURE_OF_CALL_SPECIFY.WASTE_DISPOSAL_SPECIFY,
  [NATURE_OF_CALL.OTHERS]: NATURE_OF_CALL_SPECIFY.OTHERS_SPECIFY,
} as const;

type NatureOfCallField = (typeof NATURE_OF_CALL)[keyof typeof NATURE_OF_CALL];
type NatureOfCallWithSpecify = keyof typeof NATURE_OF_CALL_SPECIFY_BY_FLAG;

const hasSpecifyField = (field: NatureOfCallField): field is NatureOfCallWithSpecify =>
  field in NATURE_OF_CALL_SPECIFY_BY_FLAG;

const VoyageDetails: React.FC = () => {
  const { formData, updateFormData, watch } = useOutletContext<VesselCallOutletContext>();

  const eta = watch(FORM_FIELDS.ETA);
  const selectedPort = watch(FORM_FIELDS.SELECT_PORT);
  const vesselCondition = watch(FORM_FIELDS.VESSEL_CONDITION) as string | undefined;
  const cargoOnboard =
    (watch(FORM_FIELDS.CARGO_ONBOARD) as VesselCallFormData[typeof FORM_FIELDS.CARGO_ONBOARD]) ??
    [];

  const cargoOperationSpecify = watch(FORM_FIELDS.CARGO_OPERATION);
  const wasteDisposalSpecify = watch(FORM_FIELDS.WASTE_DISPOSAL);
  const othersSpecify = watch(FORM_FIELDS.OTHERS);

  const natureOfCallError = (formData.formState.errors as Record<string, { message?: string }>)[
    NATURE_OF_CALL_ERROR_FIELD
  ]?.message;

  const natureOfCallChecked = Object.values(NATURE_OF_CALL).some((field) => watch(field));

  const portFacility = watch(FORM_FIELDS.PORT_FACILITY);
  const shouldShowCargoOnboard = shouldShowCargoOnboardSection(vesselCondition);

  const specifyObj = useMemo(
    () => ({
      cargoOperationSpecify,
      wasteDisposalSpecify,
      othersSpecify,
    }),
    [cargoOperationSpecify, wasteDisposalSpecify, othersSpecify],
  );

  const { options: nationalities } = useFormattedReference<Nationality>(
    REFERENCE_DATA_TYPES.NATIONALITY,
    "bff-gatepass",
  );
  const nationalitiesOptions = useMemo(
    () =>
      nationalities
        .map((n) => ({ label: n.nationality, value: n.nationality }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [nationalities],
  );

  const { options: natureOfCall } = useFormattedReference<NatureOfCall>(
    REFERENCE_DATA_TYPES.NATURE_OF_CALLS,
    "bff-vessel",
  );

  const natureOfCallOptions = useMemo(
    () =>
      natureOfCall
        .map((n) => ({ label: n.description, value: n.code }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [natureOfCall],
  );

  const { options: portCodes } = useFormattedReference<PortCode>(
    REFERENCE_DATA_TYPES.PORT_CODES,
    "bff-vessel",
  );

  const { options: ports } = useFormattedReference<PortCode>(
    REFERENCE_DATA_TYPES.PORTS,
    "bff-vessel",
  );

  const portOptions = useMemo(() => buildPortOptions(ports), [ports]);

  const methodAtImo2020ComplianceOptions = useMemo(
    () =>
      Object.values(methodAtImo2020ComplianceValues).map((method) => ({
        label: t(`${LANG_BASE}.${method}`),
        value: method,
      })),
    [t],
  );

  const { options: companies } = useFormattedReference<Company>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isShippingAgency=true`,
    "bff-vessel-call",
  );

  const companiesOptions = useMemo(
    () =>
      companies
        .map((u) => ({ label: u.companyNameEnglish, value: u.companyCode }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [companies],
  );

  const { data: fetchedVesselConditions = [] } = useQuery<VesselConditions[]>({
    queryKey: ["vessel-conditions"],
    queryFn: () => getVesselConditions("bff-vessel-call") as Promise<VesselConditions[]>,
  });

  const vesselConditionOptions = useMemo(
    () =>
      fetchedVesselConditions.map((condition) => ({
        label: String(condition.conditionDesc),
        value: String(condition.conditionCode),
      })),
    [fetchedVesselConditions],
  );

  const expectedArrivalLocationOptions = useMemo(() => {
    const options =
      portCodes
        .find((p) => p.portCode === selectedPort)
        ?.fromToLocations?.sort((a, b) => a.locationName.localeCompare(b.locationName))
        .map((l) => ({ label: l.locationName, value: l.fromToLocationCode })) || [];

    if (selectedPort) {
      options.push({ label: t(`${LANG_BASE}.other`), value: "OTHER" });
    }
    return options;
  }, [ports, selectedPort]);

  const resetField = <K extends keyof VesselCallFormData>(
    field: K,
    value: VesselCallFormData[K],
  ) => {
    formData.setValue(field, value, {
      shouldDirty: true,
      shouldValidate: false,
    });
    updateFormData(field, value);
    formData.clearErrors(field);
  };

  useEffect(() => {
    if (shouldShowCargoOnboard) {
      return;
    }

    formData.clearErrors(FORM_FIELDS.CARGO_ONBOARD);

    if (cargoOnboard.length === 0) {
      return;
    }

    resetField(
      FORM_FIELDS.CARGO_ONBOARD,
      [] as VesselCallFormData[typeof FORM_FIELDS.CARGO_ONBOARD],
    );
  }, [cargoOnboard, shouldShowCargoOnboard]);

  const handleNatureOfCallChange =
    (field: NatureOfCallField) => (key: typeof field, value: VesselCallFormData[typeof field]) => {
      const specifyField = hasSpecifyField(field)
        ? NATURE_OF_CALL_SPECIFY_BY_FLAG[field]
        : undefined;

      if (specifyField && !value) {
        resetField(specifyField, "" as VesselCallFormData[typeof specifyField]);
      }

      updateFormData(key, value);
    };

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${LANG_BASE}.form-sections.voyage-details`)}>
        <input type="hidden" {...formData.register(FORM_FIELDS.VESSEL_ID)} />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.MASTERS_NAME>
          name={FORM_FIELDS.MASTERS_NAME}
          type="text"
          label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.MASTERS_NAME}`)}
          testId="masters-name"
          onFieldChange={updateFormData}
          maxChars={50}
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.NATIONALITY>
          name={FORM_FIELDS.NATIONALITY}
          type="autocomplete"
          options={nationalitiesOptions}
          label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.NATIONALITY}`)}
          testId="nationality"
          onFieldChange={updateFormData}
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.ETA>
          name={FORM_FIELDS.ETA}
          type="date-picker"
          label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.ETA}`)}
          testId="eta"
          onFieldChange={updateFormData}
          dateConstraint="not-past"
          combinedInput={true}
          required
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.ETD>
          name={FORM_FIELDS.ETD}
          type="date-picker"
          label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.ETD}`)}
          testId="etd"
          onFieldChange={updateFormData}
          dateConstraint="not-past"
          minDate={eta ? new Date(new Date(eta).getTime() + 60 * 1000) : undefined}
          combinedInput={true}
          required
        />
        <div className="col-span-full">
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.OWNERS_MATTERS_AGENT>
            name={FORM_FIELDS.OWNERS_MATTERS_AGENT}
            type="autocomplete"
            options={companiesOptions}
            label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.OWNERS_MATTERS_AGENT}`)}
            testId="owners-matters-agent"
            onFieldChange={updateFormData}
          />
        </div>
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.LAST_PORT>
          name={FORM_FIELDS.LAST_PORT}
          type="autocomplete"
          options={portOptions}
          label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.LAST_PORT}`)}
          testId="last-port"
          onFieldChange={updateFormData}
        />
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.NEXT_PORT>
          name={FORM_FIELDS.NEXT_PORT}
          type="autocomplete"
          options={portOptions}
          label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.NEXT_PORT}`)}
          testId="next-port"
          onFieldChange={updateFormData}
        />
        <>
          <div className="col-span-1 grid grid-cols-2 gap-4">
            <FormItem<VesselCallFormData, typeof FORM_FIELDS.DRAFT_FWD>
              name={FORM_FIELDS.DRAFT_FWD}
              type="number"
              label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.DRAFT_FWD}`)}
              testId="draft-fwd"
              onFieldChange={updateFormData}
              maxDigits={5}
              decimals={3}
              canBeginWithZero={false}
            />
            <FormItem<VesselCallFormData, typeof FORM_FIELDS.DRAFT_AFT>
              name={FORM_FIELDS.DRAFT_AFT}
              type="number"
              label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.DRAFT_AFT}`)}
              testId="draft-aft"
              onFieldChange={updateFormData}
              maxDigits={5}
              decimals={3}
              canBeginWithZero={false}
            />
          </div>
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.VESSEL_CONDITION>
            name={FORM_FIELDS.VESSEL_CONDITION}
            type="autocomplete"
            // TODO: Replace with backend options when ready or keep hardcoded
            options={vesselConditionOptions}
            label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.VESSEL_CONDITION}`)}
            testId="vessel-condition"
            onFieldChange={updateFormData}
          />
        </>
        {shouldShowCargoOnboard && (
          <>
            <div className="col-span-full">
              <FormDivider />
            </div>
            <div className="font-bold text-lg">{t(`${LANG_BASE}.form-sections.cargo-onboard`)}</div>
            <CargoOnboard />
          </>
        )}
        <div className="col-span-full">
          <FormDivider />
        </div>
        <div className="col-span-full font-bold text-lg">
          {t(`${LANG_BASE}.form-sections.nature-of-call`)}
        </div>
        <div className="col-span">
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.PRIMARY_NATURE_OF_CALL_CODE>
            name={FORM_FIELDS.PRIMARY_NATURE_OF_CALL_CODE}
            type="autocomplete"
            options={natureOfCallOptions}
            label={t("vessel-call.PreArrivalNotification.primary-nature-of-call")}
            testId="primary-nature-of-call"
            onFieldChange={(key, value) => {
              updateFormData(key, value);
              const selectedLabel =
                natureOfCallOptions.find((option) => option.value === value)?.label || "";
              updateFormData(FORM_FIELDS.PRIMARY_NATURE_OF_CALL_DESC, selectedLabel);
              formData.setValue(FORM_FIELDS.PRIMARY_NATURE_OF_CALL_DESC, selectedLabel as any, {
                shouldDirty: true,
                shouldValidate: false,
              });
            }}
          />
        </div>
        <input
          type="hidden"
          {...formData.register(FORM_FIELDS.PRIMARY_NATURE_OF_CALL_DESC)}
          data-testid="primary-nature-of-call-desc"
        />
        <div className="col-span-full font-semibold text-md">
          {t(`${LANG_BASE}.form-sections.nature-of-call-secondary`)}
        </div>
        <div className="col-span-full columns-3 gap-4">
          {Object.values(NATURE_OF_CALL).map((field) =>
            field !== "primaryNatureOfCallCode" && field !== "primaryNatureOfCallDesc" ? (
              <FormItem<VesselCallFormData, typeof field>
                key={field}
                name={field}
                type="checkbox"
                label={t(`${LANG_BASE}.form-fields.${field}`)}
                testId={field}
                onFieldChange={handleNatureOfCallChange(field)}
                defaultValue={formData?.getValues(field)}
              />
            ) : null,
          )}
        </div>
        {Object.values(NATURE_OF_CALL_SPECIFY).map(
          (field) =>
            specifyObj[field] && (
              <div key={field} className="col-span-1 md:col-start-1">
                <FormItem<VesselCallFormData, typeof field>
                  name={field}
                  type="text"
                  label={t(`${LANG_BASE}.form-fields.${field}`)}
                  testId={field}
                  onFieldChange={updateFormData}
                  maxChars={100}
                />
              </div>
            ),
        )}
        {natureOfCallError && !natureOfCallChecked && (
          <div className="mt-[-2.2rem]">
            <FormHelperText error sx={{ fontWeight: 500 }}>
              {natureOfCallError}
            </FormHelperText>
          </div>
        )}
        <div className="col-span-full">
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.METHOD_AT_IMO_2020_COMPLIANCE>
            name={FORM_FIELDS.METHOD_AT_IMO_2020_COMPLIANCE}
            type="radio"
            horizontalRadioButtons
            options={methodAtImo2020ComplianceOptions}
            label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.METHOD_AT_IMO_2020_COMPLIANCE}`)}
            testId="method-at-imo-2020-compliance"
            onFieldChange={updateFormData}
          />
        </div>
        <FormItem<VesselCallFormData, typeof FORM_FIELDS.PORT_FACILITY>
          name={FORM_FIELDS.PORT_FACILITY}
          type="autocomplete"
          options={expectedArrivalLocationOptions}
          label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.PORT_FACILITY}`)}
          testId="port-facility"
          onFieldChange={(k, v) => {
            formData.clearErrors(FORM_FIELDS.PORT_FACILITY_OTHER);
            updateFormData(k, v);
          }}
        />
        {portFacility === "OTHER" && (
          <FormItem<VesselCallFormData, typeof FORM_FIELDS.PORT_FACILITY_OTHER>
            name={FORM_FIELDS.PORT_FACILITY_OTHER}
            type="text"
            label={t(`${LANG_BASE}.form-fields.${FORM_FIELDS.PORT_FACILITY_OTHER}`)}
            testId={"port-facility-others"}
            onFieldChange={updateFormData}
            maxChars={100}
          />
        )}
      </FormSection>
    </FormProvider>
  );
};

export default VoyageDetails;
